import { styled, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import { Typography } from '../Typography/Typography';

export type ActiveInactiveFilterValue = 'active' | 'inactive';

type Props = {
  value: ActiveInactiveFilterValue | null;
  onChange: (value: ActiveInactiveFilterValue | null) => void;
  /** When true, clicking the selected option deselects it (value becomes null). */
  allowDeselect?: boolean;
  activeLabel?: string;
  inactiveLabel?: string;
};

export const StatusFilter = ({
  value,
  onChange,
  allowDeselect = false,
  activeLabel = 'Active',
  inactiveLabel = 'Inactive',
}: Props) => {
  const { colors } = useTheme();

  const handleChange = (_: React.MouseEvent<HTMLElement>, newValue: ActiveInactiveFilterValue | null) => {
    if (newValue === null) {
      if (allowDeselect) onChange(null);
      return;
    }
    onChange(newValue);
  };

  return (
    <StyledToggleButtonGroup value={value} exclusive onChange={handleChange} size='small'>
      <StyledToggleButton value='active'>
        <Dot color='#439C3D' />
        <Typography
          variant='body'
          color={value === 'active' ? colors.textAccent.inverse : colors.text.main}
        >
          {activeLabel}
        </Typography>
      </StyledToggleButton>
      <StyledToggleButton value='inactive'>
        <Dot color='#6B6B80' />
        <Typography
          variant='body'
          color={value === 'inactive' ? colors.textAccent.inverse : colors.text.main}
        >
          {inactiveLabel}
        </Typography>
      </StyledToggleButton>
    </StyledToggleButtonGroup>
  );
};

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  height: 36px;

  & .MuiToggleButtonGroup-grouped {
    border: 1px solid ${({ theme }) => theme.colors.border.default};
    &:not(:first-of-type) {
      margin-left: -1px;
      border-left: 1px solid ${({ theme }) => theme.colors.border.default};
    }
  }
`;

const StyledToggleButton = styled(ToggleButton)`
  text-transform: none;
  padding: 4px 12px;
  display: flex;
  align-items: center;
  gap: 6px;

  &.Mui-selected {
    background-color: ${({ theme }) => theme.colors.surfaceAccent.default};
    border-color: ${({ theme }) => theme.colors.surfaceAccent.default};

    &:hover {
      background-color: ${({ theme }) => theme.colors.surfaceAccent.hover};
      border-color: ${({ theme }) => theme.colors.surfaceAccent.hover};
    }
  }

  &:not(.Mui-selected) {
    background-color: transparent;

    &:hover {
      background-color: ${({ theme }) => theme.colors.surfaceInteraction.selectedLight};
    }
  }
`;

const Dot = styled('span')<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  flex-shrink: 0;
`;
